'use client';

import { createContext, PropsWithChildren, useContext, useMemo } from 'react';
import { toast } from 'sonner';

import { SuperUserOnly } from '@/components/providers/admin-only';
import { ApiSchemas, useApiV1Ctx } from '@/components/providers/v1-api';
import { Button } from '@/components/ui/button';

interface Context {
  officeId: string;
  office: ApiSchemas['OfficeReadComplete'];
  regionsById: Map<string, ApiSchemas['OfficeRegionRead']>;
  skillsById: Map<string, ApiSchemas['SkillReadComplete']>;
  issuesById: Map<string, ApiSchemas['IssueRead']>;
  teamsById: Map<string, ApiSchemas['TeamRead']>;
  tagsById: Map<string, ApiSchemas['TagRead']>;
  refetch: () => void;
}

const OfficeContext = createContext<Context | null>(null);

export function useOfficeCtx() {
  const ctx = useContext(OfficeContext);
  if (!ctx) throw new Error('useOfficeCtx must be used within a OfficeProvider');
  return ctx;
}

export function useHasActiveFieldRoutesIntegration() {
  const { office } = useOfficeCtx();
  return useMemo(
    () => office.integrations.some((i) => i.provider === 'fieldroutes' && i.status === 'ACTIVE'),
    [office.integrations]
  );
}

export function OfficeProvider({ children }: PropsWithChildren) {
  const { api } = useApiV1Ctx();
  const { data, refetch, isLoading } = api.offices.getActiveOfficesActiveGet.useQuery();
  const office = useMemo(() => {
    if (!data) return null;
    const regionsById = new Map(data.regions.map((region) => [region.id, region]));
    const skillsById = new Map(data.skills.map((skill) => [skill.id, skill]));
    const issuesById = new Map(data.issues.map((issue) => [issue.id, issue]));
    const teamsById = new Map(data.teams.map((team) => [team.id, team]));
    return {
      office: {
        ...data,
        skills: data?.skills.sort((a, b) => a.name.localeCompare(b.name)),
        regions: data?.regions.sort((a, b) => a.name.localeCompare(b.name)),
        issues: data?.issues.sort((a, b) => a.name.localeCompare(b.name)),
        teams: data?.teams.sort((a, b) => a.name.localeCompare(b.name)),
      },
      regionsById,
      skillsById,
      issuesById,
      teamsById,
      tagsById: new Map(data.tags.map((tag) => [tag.id, tag])),
    };
  }, [data]);

  if (!office) {
    if (!isLoading) {
      return (
        <SuperUserOnly>
          <ResetCookieButton />
        </SuperUserOnly>
      );
    }
    return null;
  }

  return (
    <OfficeContext.Provider
      value={{
        officeId: office.office.id,
        office: office.office,
        regionsById: office.regionsById,
        skillsById: office.skillsById,
        issuesById: office.issuesById,
        teamsById: office.teamsById,
        tagsById: office.tagsById,
        refetch,
      }}
    >
      {children}
    </OfficeContext.Provider>
  );
}

function ResetCookieButton() {
  const { api } = useApiV1Ctx();
  const mutation = api.employees.resetEmployeeCookie.useMutation(
    {},
    {
      onSuccess: () => {
        toast.success('Cookie reset');
        window.location.reload();
      },
    }
  );

  return (
    <div className='flex h-screen flex-col items-center justify-center gap-4'>
      <p className='text-sm text-muted-foreground'>No office found.</p>
      <Button
        variant='outline'
        onClick={() => {
          mutation.mutate();
        }}
      >
        Reset Cookie
      </Button>
    </div>
  );
}
